import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Layer, Source } from 'react-mapbox-gl'
import isEqual from 'deep-equal'
import { throttle } from 'throttle-debounce'
import diff from '../utility/diff'
import { makeGetLayerRendered } from '../selectors/layerRenderedSelector'
import { makeGetPaint } from '../selectors/paintSelector'
import { makeGetDatetime } from '../selectors/datetimeSelector'
import DataActions from '../redux/dataRedux'
import { makeGetEventData } from '../selectors/eventDataSelector'
import makeGeoserverUrlFunction from '../utility/makeGeoserverUrlFunction'
import convertTimestamp from '../utility/convertTimestampForMapbox'
import ItemSummaryFloatContainer from '../containers/ItemSummaryFloatContainer'
import getIceDriftFilter from '../utility/getIceDriftFilter'
import iceDriftDuck from '../redux/iceDriftDuck'
import { makeGetTimestamps } from '../redux/iceDriftSelector'

const makeSlippyMapUrl = ({ timeIso, sourceLayer }) =>
      `https://s3-us-west-2.amazonaws.com/coresight-layers/${sourceLayer}/${encodeURIComponent(timeIso)}/{z}/{x}/{y}.pbf`

const getTimeFilter= time => {
  const timeIso = time ? new Date(time).toISOString() : ''
  const timestamp1 = convertTimestamp(timeIso) || "2018-05-23 11:05:15.508"
  const filter = ['==', ['get', 'timestamp1'], timestamp1]

  return filter
}

const color = [
  'interpolate',
  ['linear'],
  ['number', ['get', 'disp_m']],
  0, '#7676f4',
  10000, '#0e8f7c',
  20000, '#50e62c',         
  30000, '#e6fc0c',
  40000, '#febd09',
  50000, '#fe870b',
  60000, '#fb8a7d'
]

class ArrowLayer extends Component {
  static contextTypes = {
    map: PropTypes.object
  }

  componentDidMount () {    
    const { map } = this.context
    const { id } = this.props    
    if (!map) { return }
    
    map.on('mousemove', id, this.handleMouseMove)
    map.on('mouseout', id, this.handleMouseOut)
    // map.on('moveend', id, this.handleData)
    map.on('sourcedata', id, this.handleData)
  }

  componentWillUnmount () {    
    const { map } = this.context
    const { id } = this.props    
    if (!map) { return }
    
    map.off('mousemove', id, this.handleMouseMove)
    map.off('mouseout', id, this.handleMouseOut)
    // map.off('moveend', id, this.handleData)
    map.off('sourcedata', id, this.handleData)
  }

  handleRender = event => {
    console.log('render')
    console.log({ event })
  }

  handleData = event => {
    const { map } = this.context
    const { sourceLayer, id, layerRendered, actions } = this.props

    if (!layerRendered) {
      return null
    }

    const { isSourceLoaded } = event
    if (isSourceLoaded) {
      const allFeatures = map.querySourceFeatures(
        id,
        {
          sourceLayer: sourceLayer
        }                                               
      )

      const timestamps = getIceDriftFilter(allFeatures)

      actions.timestampsChange({ timestamps, id })
    }
  }

  handleMouseMove = event => {    
    const { map } = this.context
    const { sourceLayer } = this.props
      const { actions, layerRendered, id } = this.props
    if (!layerRendered) {
      return
    }
    const features = map.queryRenderedFeatures(event.point, {
      layers: [id]
    })
    const data = features.map(feature => feature.properties)
      if (data) {
      actions.dataUpdated(data[0])
    }
    
    map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : ''
  }

  handleMouseOut = event => {
    const { map } = this.context
    const { actions } = this.props


    map.getCanvas().style.cursor = ''
    // throttle(300, false, () => actions.dataUpdated({}), true)
    actions.dataUpdated({})
  }

  shouldComponentUpdate (nextProps) {
    if (nextProps.paint !== this.props.paint) {
      return false
    } else if (nextProps.time !== this.props.time) {
      return true
    } else if (nextProps === this.props) {
      return false
    } else if (nextProps.layerRendered !== this.props.layerRendered) {
      return true
    }

    return false
  }

  componentWillReceiveProps (props) {
    const { id, paint, layout, before } = this.props
    const { map } = this.context

    if (!isEqual(props.paint, paint)) {
      const paintDiff = diff(paint, props.paint)

      Object.keys(paintDiff).forEach(key => {
        map.getLayer(id) && paintDiff[key] && map.setPaintProperty(id, key, paintDiff[key])
        // TODO: abstract this
        if (key === 'line-opacity') {
          map.getLayer(id) && paintDiff[key] && map.setPaintProperty(`${id}-arrow`, 'text-opacity', paintDiff[key])
        }
      })
    }

    if (!isEqual(props.layout, layout)) {
      const layoutDiff = diff(layout, props.layout)

      Object.keys(layoutDiff).forEach(key => {
        map.getLayer(id) && map.setLayoutProperty(id, key, layoutDiff[key])
      })
    }

    if (before !== props.before) {
      map.moveLayer(id, props.before)
    }

    if (props.time !== this.props.time) {
      // const { time } = props      
      // const filter = getTimeFilter(time)
      const filter = null
       map.getLayer(id) && map.setFilter(id, filter)
       map.getLayer(`${id}-arrow`) && map.setFilter(`${id}-arrow`, filter)
    }
    if (props.timestamps !== this.props.timestamps) {
      if (props.timestamps.length > 1) {
        const filter = ['==', 'timestamp2', props.timestamps[0]]
        map.getLayer(id) && map.setFilter(id, filter)
        map.getLayer(`${id}-arrow`) && map.setFilter(`${id}-arrow`, filter)
      } else {
        map.getLayer(id) && map.setFilter(id, undefined)
        map.getLayer(`${id}-arrow`) && map.setFilter(`${id}-arrow`, undefined)
      }
    }
  }

  render () {
    const { id, geoserverId, urlFunction, idToken, layerRendered, paint, time, url, urls=[], minzoom, maxzoom, scheme, sourceLayer, timestamps } = this.props

    const visible = layerRendered
    if (!visible) {
      return null
    }
    
    const timeIso = time ? new Date(time).toISOString() : ''
    const timestamp1 = convertTimestamp(timeIso) || "2018-05-23 11:05:15.508"
    const layerId = geoserverId || id
    const urlString = url || urlFunction({ timeIso, sourceLayer })

    const urlsString = urls.map(url => url || urlFunction({ timeIso, sourceLayer }))

    const source = {
      type: 'vector',
      tiles: urlsString.length > 0 ? urlsString : [urlString],
      tileSize: 512,
      minzoom: minzoom,
      maxzoom: maxzoom,
      // scheme: scheme
    }

    return (
      <Fragment key={id}>
        <Source
          key={`${id}Source`}
          id={id}
          tileJsonSource={source}
          />
        <Layer
          key={`${id}Layer`}
          type='line'
          id={id}
          sourceId={id}
          sourceLayer={sourceLayer}
          paint={{
                'line-color': color,
                'line-width': 2
          }}
        />
        <Layer
          type='symbol'
          id={`${id}-arrow`}
          sourceId={id}
          sourceLayer={sourceLayer}
          layout={{
            'text-field': '^',
            'symbol-placement': 'line-center',
            'symbol-spacing': 1,
            'text-size': [
              "interpolate", ["exponential", 2], ["zoom"],
              1, 0,
              5, 10,
              10, 30,
              15, 90,
              20, 100
            ],            
            'text-keep-upright': false,
            'text-rotation-alignment': 'map',
            'text-anchor': 'top',
            'text-max-angle': 360,
            'text-rotate': 90,
            'text-padding': 0,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-justify': 'right',
            'text-offset': [0, 0.1]
          }}
          paint={{
            'text-color': color
          }}
        />
        <ItemSummaryFloatContainer id={id} />
      </Fragment>
    )
  }
}

const makeMapStateToProps = () => {
  const getLayerRendered = makeGetLayerRendered()
  const getPaint = makeGetPaint()
  const getDatetime = makeGetDatetime()
  const getEventData = makeGetEventData()
  const getTimestamps = makeGetTimestamps()

  const mapStateToProps = (state, ownProps) => {
    const { id, authId, datetimeId } = ownProps    

    return {
      idToken: state.login.idToken,
      layerRendered: getLayerRendered(
        state,
        id,
        authId || id
      ),
      paint: getPaint(state, id),
      time: state.layer.times[datetimeId || id] || undefined,
      datetime: getDatetime(state, id) || '',
      hovered: getEventData(state, id),
      timestamps : getTimestamps(state, id),
    }
  }

  return mapStateToProps
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { id } = ownProps

  return {
    actions: {
      dataUpdated: (json, event) =>
      dispatch(DataActions.dataFulfilled(json, id, event)),
      timestampsChange: ({ timestamps, id }) => dispatch(iceDriftDuck.actions.timestampsChanged({ timestamps, id }))
    }
  }
}

ArrowLayer.propTypes = {
  id: PropTypes.string.isRequired,
  authId: PropTypes.string,
  datetimeId: PropTypes.string,
  urlFunction: PropTypes.func.isRequired,
  paint: PropTypes.object,
  url: PropTypes.string,
  minzoom: PropTypes.number,
  maxzoom: PropTypes.number,
  scheme: PropTypes.string
}

ArrowLayer.defaultProps = {
  paint: { 'raster-opacity': 1 },
  urlFunction: makeSlippyMapUrl,
  minzoom: 0,
  maxzoom: 22,
  scheme: 'xyz'
}

export default connect(makeMapStateToProps, mapDispatchToProps)(ArrowLayer)



// WEBPACK FOOTER //
// ./src/layers/ArrowLayer.js